import {
  Component,
  ElementRef,
  afterRenderEffect,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';

import { AssistantWindowHost } from '@core/services/assistant/assistant-window-host';
import { AssistantService } from '@core/services/assistant/assistant.service';
import { ICONS } from '@core/services/icons/icons.service';
import { showsContext } from '@core/utils/assistant-page-context.utils';

import { SelectComponent } from '@shared/components/select/select.component';

import { promptHistory } from './assistant-composer/prompt-history.utils';
import { AssistantContextLineComponent } from './assistant-context/assistant-context-line.component';
import { AssistantMessageComponent } from './assistant-message/assistant-message.component';
import { AssistantSettingsComponent } from './assistant-settings/assistant-settings.component';
import { AssistantTabsComponent } from './assistant-tabs/assistant-tabs.component';

@Component({
  selector: 'app-assistant-panel',
  imports: [
    TranslatePipe,
    SelectComponent,
    AssistantContextLineComponent,
    AssistantMessageComponent,
    AssistantSettingsComponent,
    AssistantTabsComponent
  ],
  templateUrl: './assistant-panel.component.html',
  host: {
    '[class]': 'hostClass()',
    role: 'complementary',
    '[attr.aria-label]': 'panelLabel()',
    '(keydown.escape)': 'onEscape($event)',
    '(click)': 'onClick($event)'
  },
  styles: ``
})
export class AssistantPanelComponent {
  private readonly translate = inject(TranslateService);
  private readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(map((e) => e.lang)),
    {
      initialValue: this.translate.getCurrentLang()
    }
  );
  protected readonly panelLabel = computed(() => {
    this.currentLang();
    return this.translate.instant('assistant.title');
  });
  protected readonly assistant = inject(AssistantService);
  protected readonly host = inject(AssistantWindowHost);
  // Below lg the thin panel already overlays the page, so the side sizes differ only from lg up.
  protected readonly hostClass = computed(() => {
    switch (this.assistant.presentation()) {
      case 'window':
        return 'bg-elevated flex h-full min-h-0 w-full flex-col';
      case 'focus':
        return 'bg-elevated absolute inset-0 z-30 flex min-h-0 flex-col';
      default:
        return (
          'bg-elevated border-default absolute inset-y-0 right-0 z-30 flex min-h-0 w-full max-w-[400px] flex-col border-l shadow-lg ' +
          (this.assistant.panelSize() === 'large'
            ? 'lg:absolute lg:w-1/2 lg:min-w-[400px] lg:max-w-none'
            : 'lg:static lg:w-[400px] lg:shadow-none')
        );
    }
  });
  protected readonly settingsLink = computed(
    () =>
      `/admin/ai-assistant?data_space_id=${this.assistant.homeDataSpaceId() ?? this.assistant.dataSpaceId()}`
  );
  protected readonly wide = computed(() => this.assistant.presentation() !== 'side');
  protected readonly column = computed(() =>
    this.wide() ? 'mx-auto w-full max-w-3xl' : ''
  );
  protected readonly focusLabel = computed(() => {
    this.currentLang();
    return this.translate.instant(
      this.assistant.presentation() === 'focus' ? 'assistant.exit_focus' : 'assistant.focus'
    );
  });
  protected readonly popOutLabel = computed(() => {
    this.currentLang();
    return this.translate.instant(
      this.assistant.anyStreaming() ? 'assistant.wait_for_reply' : 'assistant.pop_out'
    );
  });
  protected readonly sizeToggleLabel = computed(() => {
    this.currentLang();
    return this.translate.instant(
      this.assistant.panelSize() === 'large' ? 'assistant.shrink' : 'assistant.expand'
    );
  });
  protected readonly modeItems = computed(() => {
    this.currentLang();
    return (['ask', 'auto_writes', 'auto_all'] as const).map((id) => ({
      id,
      label: this.translate.instant(`assistant.approval_mode.${id}`),
      detail: this.translate.instant(`assistant.approval_mode.hint_${id}`)
    }));
  });
  protected readonly ICONS = ICONS;
  // Once per transcript change rather than once per message on every change detection.
  protected readonly captioned = computed(() => {
    const messages = this.assistant.messages();
    return messages.map((_, i) => showsContext(messages, i));
  });
  protected readonly showSettings = signal(false);
  protected readonly modelItems = computed(() =>
    (this.assistant.selectedConnection()?.models ?? []).map((m) => ({ id: m, label: m }))
  );
  private readonly history = computed(() =>
    promptHistory(this.assistant.tabs(), this.assistant.activeTabId())
  );
  // Index in `history` of the prompt the box shows after ↑ / ↓; null while the user writes their own.
  private recalled: number | null = null;
  private readonly scroller = viewChild<ElementRef<HTMLElement>>('scroller');
  private readonly input = viewChild<ElementRef<HTMLTextAreaElement>>('input');
  private focusedInput = false;
  // Follow the stream only while the reader is at the end; someone rereading an earlier answer stays put.
  protected readonly following = signal(true);

  constructor() {
    // The textarea only exists once the config has loaded, so focus waits for it.
    effect(() => {
      const el = this.input()?.nativeElement;
      if (el && !this.focusedInput) {
        this.focusedInput = true;
        el.focus();
      }
    });
    effect(() => {
      this.assistant.messages();
      const el = this.scroller()?.nativeElement;
      if (el && untracked(() => this.following()))
        queueMicrotask(() => (el.scrollTop = el.scrollHeight));
    });
    // After render, so the measurement sees the value the template just wrote, including the empty one after a send.
    afterRenderEffect(() => {
      this.assistant.draft();
      const el = this.input()?.nativeElement;
      if (!el) return;
      el.style.height = 'auto';
      // scrollHeight leaves out the border, which the border-box height has to include; the CSS max-height caps it.
      el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
    });
  }

  protected onScroll(el: HTMLElement): void {
    this.following.set(el.scrollHeight - el.scrollTop - el.clientHeight < 24);
  }

  protected jumpToLatest(): void {
    const el = this.scroller()?.nativeElement;
    if (el) el.scrollTop = el.scrollHeight;
    this.following.set(true);
  }

  // The dropdown singleton closes an open app-select on the same Escape from a document listener, so
  // without this check one key press would close both the select's list and the whole panel.
  protected onEscape(event: Event): void {
    if ((event.target as Element | null)?.closest('[aria-expanded="true"]')) return;
    // While a reply streams, Escape stops it, as in Claude Code; the next one leaves focus, then closes.
    if (this.assistant.streaming()) {
      this.assistant.stop();
      return;
    }
    const presentation = this.assistant.presentation();
    if (presentation === 'focus') this.assistant.toggleFocus();
    else if (presentation === 'side') this.close();
  }

  // Reply links open a new browser tab by default. A link into Galia instead opens in the app: in this
  // tab, or in the linked tab when this is the separate window. Modified clicks keep the browser's meaning.
  protected onClick(event: MouseEvent): void {
    const anchor = (event.target as Element | null)?.closest<HTMLAnchorElement>('a[href]');
    if (
      !anchor ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    const url = new URL(anchor.href, location.href);
    if (url.origin !== location.origin) return;
    event.preventDefault();
    this.assistant.openInApp(url.pathname + url.search + url.hash);
  }

  protected close(): void {
    this.assistant.togglePanel();
    document.getElementById('assistant-toggle')?.focus();
  }

  protected submit(event?: Event): void {
    event?.preventDefault();
    const text = this.assistant.draft();
    if (!text.trim() || !this.assistant.canSend()) return;
    this.assistant.draft.set('');
    this.recalled = null;
    this.following.set(true);
    void this.assistant.send(text);
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) this.submit(event);
    else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') this.recall(event);
  }

  // ↑ and ↓ walk through earlier prompts only while the box is empty or still shows a recalled prompt
  // untouched; once the user types, the keys move the cursor again.
  private recall(event: KeyboardEvent): void {
    const history = this.history();
    const draft = this.assistant.draft();
    const at = this.recalled;
    const browsing = at !== null && history[at] === draft;
    if (!browsing && draft !== '') return;
    const up = event.key === 'ArrowUp';
    if (!browsing && !up) return;
    event.preventDefault();
    const next = !browsing ? 0 : up ? at + 1 : at - 1;
    if (next >= history.length) return;
    this.recalled = next < 0 ? null : next;
    this.assistant.draft.set(next < 0 ? '' : history[next]);
  }
}
